import type { ColumnConfig } from '../../../hooks/useColumnConfig';

/**
 * Map a grid column to its backend export key.
 * If exportKey is explicitly null, the column is not exportable.
 * If exportKey is undefined, fall back to the column id.
 */
export function getExportKey(col: ColumnConfig): string | null {
  if (col.exportKey === null) return null;
  return col.exportKey ?? col.id;
}

/**
 * Get exportable column keys from grid column configs.
 * Filters out non-exportable columns (exportKey === null) and returns backend export keys.
 */
function getExportKeysFromGrid(gridColumns: ColumnConfig[]): string[] {
  return gridColumns
    .map(getExportKey)
    .filter((key): key is string => key !== null);
}

/**
 * Get exportable column keys for currently visible grid columns.
 * Preserves the visible order from the grid.
 */
export function getVisibleExportKeys(gridColumns: ColumnConfig[], visibleIds: string[]): string[] {
  return visibleIds
    .map(id => gridColumns.find(c => c.id === id))
    .filter((col): col is ColumnConfig => col !== undefined)
    .map(getExportKey)
    .filter((key): key is string => key !== null);
}

/**
 * "All columns" = every deliverable field for this entity/view: the UNION of the
 * grid's export keys and the backend's available column keys. This handles both
 * directions the audit found (MED-1234): grid ⊂ backend (e.g. participants, where
 * the grid shows fewer than the 44 exportable columns) and grid ⊃ backend (survey
 * tabs, whose grids carry dynamic q_<question_id> columns the static backend list
 * lacks). Deduped; order is backend-first, then any grid-only keys.
 */
export function getAllColumnKeys(
  gridColumns: ColumnConfig[] | undefined,
  backendColumns: { key: string }[],
): string[] {
  const gridKeys = gridColumns ? getExportKeysFromGrid(gridColumns) : [];
  return Array.from(new Set([...backendColumns.map(c => c.key), ...gridKeys]));
}

/**
 * MDF-271: the "All columns" keys for a modal. A tab that offers only its grid
 * columns (gridColumnsOnly, the 1:1s tab) ignores the backend list: that list
 * belongs to the export entity (survey_responses), not to the tab.
 */
export function allColumnKeysFor(
  gridColumns: ColumnConfig[] | undefined,
  backendColumns: { key: string }[],
  gridColumnsOnly = false,
): string[] {
  return getAllColumnKeys(gridColumns, gridColumnsOnly ? [] : backendColumns);
}
